import { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  Chip,
  LinearProgress,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import { ExpandMore, ExpandLess, InfoOutlined } from '@mui/icons-material';
import { OCIFactorsListProps } from '../../types/oci.types';

export default function OCIFactorsList({
  factors,
  expanded: initialExpanded = false,
}: OCIFactorsListProps) {
  const [expanded, setExpanded] = useState(initialExpanded);

  // Return null if factors is not a valid array
  if (!factors || !Array.isArray(factors) || factors.length === 0) {
    console.warn('Invalid or empty factors array:', factors);
    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No competitiveness factors available
        </Typography>
      </Paper>
    );
  }

  // Sort factors by contribution (highest first)
  const sortedFactors = [...factors].sort((a, b) => b.contribution - a.contribution);
  const topFactors = sortedFactors.slice(0, 3);
  const allFactors = sortedFactors;

  const displayFactors = expanded ? allFactors : topFactors;

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlined color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Competitiveness Factors
          </Typography>
        </Box>
        {factors.length > 3 && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: 'primary.main' }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>

      <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
        Top factors contributing to this page's competitiveness score
      </Typography>

      <List disablePadding>
        {displayFactors.map((factor, index) => (
          <Box key={factor.name}>
            <ListItem
              sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                py: 1.5,
                px: 0,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`#${index + 1}`}
                    size="small"
                    variant="outlined"
                    sx={{ width: 40, height: 20, fontSize: '0.7rem' }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {factor.name}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {factor.contribution.toFixed(1)}%
                </Typography>
              </Box>

              <Box sx={{ width: '100%', mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={factor.contribution}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1,
                      bgcolor: index === 0 ? 'error.main' : index === 1 ? 'warning.main' : 'info.main',
                    },
                  }}
                />
              </Box>

              <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                {factor.description}
              </Typography>

              {/* Dynamic layman explanation based on contribution level */}
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                {(() => {
                  const contrib = factor.contribution;
                  const isHigh = contrib > 30;
                  const isMedium = contrib > 15 && contrib <= 30;
                  
                  if (factor.name === 'click_velocity') {
                    if (isHigh) return `💡 Your daily clicks are VERY UNPREDICTABLE (${contrib.toFixed(1)}%). This makes revenue forecasting difficult. Example: Monday gets 100 clicks, Tuesday gets 300, Wednesday drops to 150.`;
                    if (isMedium) return `💡 Your daily clicks vary moderately (${contrib.toFixed(1)}%). Some fluctuation is normal, but watch for increasing volatility.`;
                    return `💡 Your daily clicks are relatively stable (${contrib.toFixed(1)}%). This is good - traffic is predictable.`;
                  }
                  
                  if (factor.name === 'impression_growth') {
                    if (isHigh) return `💡 Your search visibility FLUCTUATES SIGNIFICANTLY (${contrib.toFixed(1)}%). Sometimes you appear 10K times/day, other times 50K - this indicates an unstable SERP position.`;
                    if (isMedium) return `💡 Your search visibility varies somewhat (${contrib.toFixed(1)}%). Could be seasonal trends or competitors entering/leaving the SERP.`;
                    return `💡 Your search visibility is consistent (${contrib.toFixed(1)}%). You're showing up for the same volume of searches regularly.`;
                  }
                  
                  if (factor.name === 'position_change') {
                    if (isHigh) return `💡 Your ranking BOUNCES AROUND FREQUENTLY (${contrib.toFixed(1)}%). Example: #3 today, #9 tomorrow, #5 next week. Competitors are actively pushing you up and down.`;
                    if (isMedium) return `💡 Your ranking shifts moderately (${contrib.toFixed(1)}%). Not rock-solid, but not chaotic either. Monitor for worsening trends.`;
                    return `💡 Your ranking is quite stable (${contrib.toFixed(1)}%). You're holding your position well against competitors.`;
                  }
                  
                  if (factor.name === 'ctr_trend') {
                    if (isHigh) return `💡 Your click-through rate is CHANGING SIGNIFICANTLY (${contrib.toFixed(1)}%). Either improving (good) or declining (bad). If declining, your title/description may be losing appeal to searchers.`;
                    if (isMedium) return `💡 Your CTR shows some trending (${contrib.toFixed(1)}%). Watch whether it's moving up or down - this affects total clicks even if impressions stay the same.`;
                    return `💡 Your CTR is consistent (${contrib.toFixed(1)}%). The percentage of people clicking when they see your listing is stable.`;
                  }
                  
                  if (factor.name === 'momentum_state') {
                    if (contrib === 0) return `💡 Your page is PERFECTLY STABLE (${contrib.toFixed(1)}%). No clear upward or downward trend - everything is flat. This is neutral, not good or bad.`;
                    if (isHigh) return `💡 Your page has STRONG DIRECTIONAL MOMENTUM (${contrib.toFixed(1)}%). Everything is either consistently growing (RISING) or consistently declining (DECLINING).`;
                    return `💡 Your page shows some directional trend (${contrib.toFixed(1)}%). There's a pattern emerging, but it's not strong yet.`;
                  }
                  
                  // Fallback for other factor names
                  if (factor.name === 'impression_volatility') {
                    if (isHigh) return `💡 High daily fluctuation (${contrib.toFixed(1)}%) in search appearances. Indicates competitive SERP with frequent ranking changes.`;
                    return `💡 Your impression volatility is ${contrib.toFixed(1)}%. Shows how much your daily search appearances vary.`;
                  }
                  
                  if (factor.name === 'position_variance') {
                    if (isHigh) return `💡 Rankings are very unstable (${contrib.toFixed(1)}%). Active competition for keywords makes it hard to hold your spot.`;
                    return `💡 Position variance is ${contrib.toFixed(1)}%. Measures how consistently you hold your ranking.`;
                  }
                  
                  if (factor.name === 'ctr_responsiveness') {
                    if (isHigh) return `💡 CTR changes dramatically with position shifts (${contrib.toFixed(1)}%). Users are very sensitive to ranking - intense click competition.`;
                    return `💡 CTR responsiveness is ${contrib.toFixed(1)}%. Shows how much clicks change when rankings move.`;
                  }
                  
                  if (factor.name === 'experiment_win_rate') {
                    if (isHigh) return `💡 Historical tests show it's hard to improve (${contrib.toFixed(1)}%). This page may be saturated - optimization opportunities are limited.`;
                    return `💡 Experiment win rate is ${contrib.toFixed(1)}%. Based on historical A/B test success rates.`;
                  }
                  
                  return `💡 This factor contributes ${contrib.toFixed(1)}% to the overall competitiveness score.`;
                })()}
              </Typography>

              {factor.details && (
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 1,
                    borderRadius: 1,
                    width: '100%',
                    mt: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    {factor.details}
                  </Typography>
                </Box>
              )}

              {factor.threshold !== undefined && (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" color="textSecondary">
                    Value: <strong>{factor.value.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    •
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Threshold: <strong>{factor.threshold.toFixed(2)}</strong>
                  </Typography>
                </Box>
              )}
            </ListItem>
            {index < displayFactors.length - 1 && <Divider />}
          </Box>
        ))}
      </List>

      <Collapse in={expanded && factors.length > 3}>
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="textSecondary">
            Showing all {factors.length} factors
          </Typography>
        </Box>
      </Collapse>

      {!expanded && factors.length > 3 && (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: 'pointer', fontWeight: 500 }}
            onClick={() => setExpanded(true)}
          >
            Show {factors.length - 3} more factors →
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
